import { Component, OnInit } from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { first } from 'rxjs/operators';

import { PermissionsService } from '@app/login/_services/permissions.service';
import { SuspiciousTechnicalInspectionService } from '@app/technical-inspections/_services/suspicious-technical-inspection.service';
import { ListSuspiciousTechnicalInspection } from '@app/technical-inspections/_models/list-suspicious-technical-inspection.model';
import { ExportService } from '@services/export.service';

import { convertDateToString, convertObjDateToString, prepareDateForDb, prepareDateForForm } from '@app/_utils/date-util';
import { displayErrorFromUnknown } from '@app/_utils/error-util';
import { BaseSortableSearchComponent } from '@components/_base/base-search/base-sortable-search.component';
import { SortingPagingData } from '@helpers/sorting-paging-data';

@Component({
  selector: 'app-list-suspicious-technical-inspections',
  templateUrl: './list-suspicious-technical-inspections.component.html',
  styleUrls: ['./list-suspicious-technical-inspections.component.css']
})
export class ListSuspiciousTechnicalInspectionsComponent extends BaseSortableSearchComponent<ListSuspiciousTechnicalInspection> implements OnInit {

  suspiciousInspections:     ListSuspiciousTechnicalInspection[] = [];
  suspiciousInspectionsCopy: ListSuspiciousTechnicalInspection[] = [];
  fromDateStr: string;
  toDateStr:   string;
  loadPdfOrExcel = false;

  /** Matches the values the backend accepts; anything else there reads as unresolved. */
  readonly resolvedFilterOptions = ['UNRESOLVED', 'RESOLVED', 'ALL'];

  searchForm = this.formBuilder.group({
    registrationNumber: [''],
    fromDateSearch:     [''],
    toDateSearch:       [''],
    // Unresolved is the work queue - a row settled by a corrected inspection is kept as the
    // audit trail but is no longer something anyone needs to act on.
    resolvedFilter:     ['UNRESOLVED'],
  });

  constructor(
    private formBuilder:      FormBuilder,
    public  perms:            PermissionsService,
    private suspiciousService: SuspiciousTechnicalInspectionService,
    private exportService:    ExportService,
    private translateService: TranslateService,
  ) {
    super();
  }

  ngOnInit() {
    const today = new Date();
    this.searchForm.get('fromDateSearch')?.patchValue(prepareDateForForm(today));
    this.searchForm.get('toDateSearch')?.patchValue(prepareDateForForm(today));
    this.onSubmitSearchForm();
  }

  onSubmitSearchForm() {
    this.loadSuspiciousInspections();
    this.loadTotalElements();
  }

  onSubmitSearchFormSort(event: any) {
    this.sortingPaging.sortAsc = event.sortAsc;
    this.sortingPaging.sortBy  = event.sortBy;
    this.loadSuspiciousInspections();
    this.loadTotalElements();
  }

  pageChanged(page: number) {
    this.sortingPaging.pageNumber = page;
    this.loadSuspiciousInspections();
  }

  clearFilters() {
    this.searchForm.reset();
    const today = new Date();
    this.searchForm.get('fromDateSearch')?.patchValue(prepareDateForForm(today));
    this.searchForm.get('toDateSearch')?.patchValue(prepareDateForForm(today));
    // reset() blanks this, and a blank is dropped from the request - restore it so clearing
    // returns to the work queue rather than leaning on the backend's default.
    this.searchForm.get('resolvedFilter')?.patchValue('UNRESOLVED');
    this.fromDateStr = "";
    this.toDateStr   = "";
    this.onSubmitSearchForm();
  }

  loadSuspiciousInspections() {
    this.dataLoaded = false;
    this.prepareDates();

    const searchForm = Object.assign({}, this.searchForm.value);
    delete searchForm.fromDateSearch;
    delete searchForm.toDateSearch;

    this.suspiciousService.getPagableSuspiciousTechnicalInspections(this.sortingPaging, searchForm, this.fromDateStr, this.toDateStr)
      .pipe(first())
      .subscribe(resp => {
        this.suspiciousInspections = resp.content;
        this.sortingPaging.fromRow = resp.fromRow;
        this.sortingPaging.toRow   = resp.toRow;
        this.dataLoaded = true;
      });
  }

  loadTotalElements() {
    const searchForm = Object.assign({}, this.searchForm.value);
    delete searchForm.fromDateSearch;
    delete searchForm.toDateSearch;
    this.suspiciousService.getTotalElements(this.sortingPaging, searchForm, this.fromDateStr, this.toDateStr)
      .subscribe(resp => this.sortingPaging.totalElements = resp);
  }

  private prepareDates() {
    const fromVal = this.searchForm.get('fromDateSearch')?.value;
    this.fromDateStr = fromVal ? prepareDateForDb(fromVal) : "";

    const toVal = this.searchForm.get('toDateSearch')?.value;
    this.toDateStr = toVal ? prepareDateForDb(toVal) : "";
  }

  async exportExcel() {
    this.loadPdfOrExcel = true;
    try {
      this.exportService.exportAsExcelFile(
        await this.prepareData(),
        this.getHeadings(),
        this.getFilterBody(),
        this.getFilterHeading(),
        this.translateService.instant('suspiciousTechnicalInspection.title'));
    } catch (err) {
      displayErrorFromUnknown(err);
    } finally {
      this.loadPdfOrExcel = false;
    }
  }

  async exportPDF() {
    this.loadPdfOrExcel = true;
    try {
      this.exportService.exportAsPdfFile(
        await this.prepareData(),
        this.getHeadings(),
        this.getFilterBody(),
        this.getFilterHeading(),
        this.translateService.instant('suspiciousTechnicalInspection.title'),
        convertDateToString(new Date()),
        undefined,
        undefined,
        true);
    } catch (err) {
      displayErrorFromUnknown(err);
    } finally {
      this.loadPdfOrExcel = false;
    }
  }

  private async prepareData() {
    this.prepareDates();

    const sortingPagingCopy = new SortingPagingData(this.sortingPaging.totalElements);
    sortingPagingCopy.pageSize  = this.sortingPaging.totalElements;
    sortingPagingCopy.sortBy    = this.sortingPaging.sortBy;
    sortingPagingCopy.sortAsc   = this.sortingPaging.sortAsc;

    const searchForm = Object.assign({}, this.searchForm.value);
    delete searchForm.fromDateSearch;
    delete searchForm.toDateSearch;

    const result = await this.suspiciousService
      .getPagableSuspiciousTechnicalInspections(sortingPagingCopy, searchForm, this.fromDateStr, this.toDateStr)
      .pipe(first()).toPromise();

    this.suspiciousInspectionsCopy = result?.content ?? [];

    return this.suspiciousInspectionsCopy.map(item => [
      item.technicalPointShortName,
      item.technicalInspectionCreatedDate,
      item.regNumber,
      item.reservationFinishedTimestamp,
      item.lastCheck,
      item.nextInspectionDate,
      item.resolvedAt,
      item.resolvedByRegNumber,
    ]);
  }

  private getHeadings(): string[][] {
    return [[
      this.translateService.instant('suspiciousTechnicalInspection.technicalPoint'),
      this.translateService.instant('suspiciousTechnicalInspection.createdDate'),
      this.translateService.instant('suspiciousTechnicalInspection.regNumber'),
      this.translateService.instant('suspiciousTechnicalInspection.reservationFinished'),
      this.translateService.instant('suspiciousTechnicalInspection.lastCheck'),
      this.translateService.instant('suspiciousTechnicalInspection.nextInspectionDate'),
      this.translateService.instant('suspiciousTechnicalInspection.resolvedAt'),
      this.translateService.instant('suspiciousTechnicalInspection.resolvedByRegNumber'),
    ]];
  }

  private getFilterHeading(): string[][] {
    return [
      [this.translateService.instant('exportData.filters.chosenFilters')],
      [
        this.translateService.instant('suspiciousTechnicalInspection.regNumber'),
        this.translateService.instant('suspiciousTechnicalInspection.fromDate'),
        this.translateService.instant('suspiciousTechnicalInspection.toDate'),
        this.translateService.instant('suspiciousTechnicalInspection.resolvedFilter'),
      ],
    ];
  }

  private getFilterBody(): any[] {
    const resolvedFilter = this.searchForm.get('resolvedFilter')?.value ?? 'UNRESOLVED';
    return [[
      this.searchForm.get('registrationNumber')?.value ?? '',
      convertObjDateToString(this.searchForm.get('fromDateSearch')?.value) ?? '',
      convertObjDateToString(this.searchForm.get('toDateSearch')?.value) ?? '',
      this.translateService.instant('suspiciousTechnicalInspection.resolvedFilterOption.' + resolvedFilter),
    ]];
  }
}
